import axios from 'axios';
import { API_BASE_URL } from '../utils/constants';
import { getAuthToken } from '../utils/storage';
import { extractApiError } from '../utils/apiError';

/**
 * Super-admin (STAFF) endpoints to manage a customer's portal login.
 * Uses the staff auth token (NOT the portal token).
 */
const adminAPI = axios.create({
  baseURL: `${API_BASE_URL}/api/v1`,
  headers: { 'Content-Type': 'application/json' },
});

adminAPI.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
}, (error) => Promise.reject(error));

const ok = (res) => ({ success: true, data: res.data });
const fail = (e) => ({ success: false, error: extractApiError(e), status: e.response?.status });

const customerPortalAdminService = {
  /** GET /customers/{id}/login-status → CustomerLoginStatus */
  async getLoginStatus(customerId) {
    try { return ok(await adminAPI.get(`/customers/${customerId}/login-status`)); }
    catch (e) { return fail(e); }
  },
  /** POST /customers/{id}/enable-login { login_email?, login_mobile? } (at least one) */
  async enableLogin(customerId, { login_email, login_mobile }) {
    const body = {};
    if (login_email) body.login_email = login_email;
    if (login_mobile) body.login_mobile = login_mobile;
    try { return ok(await adminAPI.post(`/customers/${customerId}/enable-login`, body)); }
    catch (e) { return fail(e); }
  },
  /** POST /customers/{id}/set-password { password } (min 8 chars) */
  async setPassword(customerId, password) {
    try { return ok(await adminAPI.post(`/customers/${customerId}/set-password`, { password })); }
    catch (e) { return fail(e); }
  },
  /** POST /customers/{id}/disable-login — also kills active portal sessions */
  async disableLogin(customerId) {
    try { return ok(await adminAPI.post(`/customers/${customerId}/disable-login`)); }
    catch (e) { return fail(e); }
  },

  // --- Invite → signup → approval flow (super-admin) ---

  /**
   * POST /customers/invite — invite a new customer with minimal info.
   * Body needs owner_name/company_name/customer_type + at least one of
   * login_email / login_mobile. The temp password is delivered to the customer,
   * never returned. Response carries invited_channels / throttled_channels.
   */
  async invite(body) {
    try { return ok(await adminAPI.post('/customers/invite', body)); }
    catch (e) { return fail(e); }
  },

  /** POST /customers/{id}/resend-invite — fresh temp password, re-sent. 429 = throttled (password NOT rotated). */
  async resendInvite(customerId) {
    try { return ok(await adminAPI.post(`/customers/${customerId}/resend-invite`)); }
    catch (e) { return fail(e); }
  },

  /** GET /customers/pending-approval — paginated queue of profile_submitted customers. */
  async getPendingApproval({ skip = 0, limit = 20 } = {}) {
    try { return ok(await adminAPI.get('/customers/pending-approval', { params: { skip, limit } })); }
    catch (e) { return fail(e); }
  },

  /** POST /customers/{id}/approve — requires profile_submitted (else 400). */
  async approve(customerId) {
    try { return ok(await adminAPI.post(`/customers/${customerId}/approve`)); }
    catch (e) { return fail(e); }
  },

  /** POST /customers/{id}/reject { reason? } — non-terminal; customer can resubmit. */
  async reject(customerId, reason) {
    const body = reason ? { reason } : {};
    try { return ok(await adminAPI.post(`/customers/${customerId}/reject`, body)); }
    catch (e) { return fail(e); }
  },
};

export default customerPortalAdminService;
